import React, { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
// project imports
import Profile from './Profile';
import Localization from './Localization';
import Notification from './Notification';
import MegaMenuSection from './MegaMenuSection';

import useConfig from 'hooks/useConfig';
import { MenuOrientation, DRAWER_COLLAPSE_BREAKPOINT } from 'config';
import DrawerHeader from 'layout/Dashboard/Drawer/DrawerHeader';
import { Typography } from '@mui/material';
import { getSubRoot } from "@/utils/utils";
import CustomSplitButton from "@/pages/custom-components/CommonComponents/CustomSplitButton";
import { useNavigate } from "react-router";
import {
  CURRENT_SPORT, defaultContentSpecificParams,
  SUPPORTED_CONTENTS, TARGET_APP_BUILD, TARGET_APP_BUILD_VALUE
} from "@/constants/globalConstants";
import { useLocation } from "react-router-dom";
import { useContentSpecificParams } from "@/contexts/ContentSpecificParamsContext";
import { useSelectedCard } from "@/contexts/SelectedCardContext";
import { useMaterializationSettings } from "@/contexts/MaterializationSettingsContext";
import { getSubRootName, sportIconMap } from '../../../../utils/utils'
import { useEditorialAssets } from "@/contexts/EditorialAssetsContext";
import {useEditState} from "@/contexts/editor/EditStateContext";
import Button from "@mui/material/Button";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {useRightDrawer} from "@/contexts/application/RightDrawerContext";
import {useChat} from "@/contexts/ChatContext";
import {enqueueSnackbar} from "notistack";
import {useKeyMomentActiveFilters} from "@/contexts/application/KeyMomentActiveFilters";
import {useSelectedContent} from "@/contexts/SelectedContentContext";
import {ModeStatus} from "@/types/editorTypes";

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const { menuOrientation } = useConfig();
  const { setContentSpecificParams } = useContentSpecificParams()
  const {setKeyMomentActiveFilters} = useKeyMomentActiveFilters()
  const { resetCardContext, setLogicClipName } = useSelectedCard();
  const { resetMaterializationSettings } = useMaterializationSettings()
  const { resetEditorialAssetSelection } = useEditorialAssets()
  const { emptyWindowLandmarks } = useEditState()
  const { toggleDrawer } = useRightDrawer()
  const { openConversation } = useChat()
  const { setSelectedEventId, setSelectedEventName, setMode } = useSelectedContent()

  const [contentType, setContentType] = React.useState(SUPPORTED_CONTENTS[CURRENT_SPORT].MATCH_HIGHLIGHT);

  const navigate = useNavigate()
  
  const downLG = useMediaQuery((theme) => theme.breakpoints.down(DRAWER_COLLAPSE_BREAKPOINT));

  const localization = useMemo(() => <Localization />, []);

  const megaMenu = useMemo(() => <MegaMenuSection />, []);

  const location = useLocation();

  const getLogoFromUrl = () => {
    return sportIconMap[getSubRootName(location.pathname)];
  }

  // Modificato per gestire anche la navigazione verso la library
  const handleActionClick = async (selectedContentType = contentType) => {
    if (selectedContentType === 'GO_TO_LIBRARY') {
      setKeyMomentActiveFilters({})
      // the library of the section the user is in: clips/library from the clips pages, keymoments/library otherwise
      const section = location.pathname.split('/')[2] === 'clips' ? 'clips' : 'keymoments';
      navigate(`${getSubRoot(location.pathname)}/${section}/library`);
      setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
      resetCardContext();
      resetMaterializationSettings()
      resetEditorialAssetSelection()
      return;
    }

    if (selectedContentType === 'CREATE_KEY_MOMENT') {
      setMode(ModeStatus.CREATE)
      navigate(getSubRoot(location.pathname) + '/keymoments/editor');
      setSelectedEventName("")
      setSelectedEventId(null)
      emptyWindowLandmarks()
      setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
      resetCardContext();
      resetMaterializationSettings()
      resetEditorialAssetSelection()
      return;
    }

    const basePath = `${getSubRoot(window.location.pathname)}/contentAggregation/${selectedContentType.toLowerCase()}`;
    setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][selectedContentType]);
    resetCardContext();
    setLogicClipName('');
    resetMaterializationSettings()
    resetEditorialAssetSelection()
    navigate(basePath)
  }

  function formatContentLabel(contentType) {
    const formattedWords = contentType
        .toLowerCase()
        .split("_")
        .map(word => {
          const cleanWord = word.trim();
          if (cleanWord === "a") return "a";
          return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
        })
        .join(" ");

    return "Create a " + formattedWords;
  }

  // Prepariamo le opzioni per lo split button includendo "Go To Library" alla fine
  const splitButtonOptions = [
    ...Object.keys(SUPPORTED_CONTENTS[CURRENT_SPORT]).map(sp => ({
      id: sp,
      label: formatContentLabel(sp)
    })),
    { id: 'CREATE_KEY_MOMENT', label: 'Create a Key Moment' },
    { id: 'GO_TO_LIBRARY', label: 'Go To Library' }
  ];

  const onAIHelpClick = () => {
    try{
      toggleDrawer()
      openConversation(false)
    }catch (e){
      console.error(e)
      enqueueSnackbar("Error Starting Conversation with AI Agent");
    }
  }

  return (
      <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 2, // padding orizzontale, allineato al padding del contenuto pagina (Dashboard main: p:2)
          }}
      >
        {/* Lato sinistro */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {menuOrientation === MenuOrientation.HORIZONTAL && !downLG && <DrawerHeader open={true} />}
          <Typography
              variant="h3"
              sx={{ letterSpacing: '0.3em', display: 'flex', alignItems: 'center', gap: 3 }}
          >
            <img src={getLogoFromUrl()?.icon} alt="Soccer Icon" style={{ width: 45, height: 45 }} />
            {getLogoFromUrl()?.sport}
          </Typography>
          {/* {!downLG && <Search />} */}
          {/* {!downLG && megaMenu} */}
          {/* {!downLG && localization} */}
        </Box>

        {/* Lato destro */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {TARGET_APP_BUILD === TARGET_APP_BUILD_VALUE.BACKUP ? <></> : <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={(theme) => ({ whiteSpace: 'nowrap', fontSize: theme.typography.button.fontSize, fontWeight: theme.typography.button.fontWeight })}>Quick action:</Typography>
              <CustomSplitButton
                  options={splitButtonOptions}
                  value={contentType}
                  onChange={(item)=> {
                    setContentType(item.id)
                    handleActionClick(item.id)
                  }}
                  onClickMain={({ id }) => handleActionClick(id)}
                  mainButtonProps={{ sx: { textTransform: 'none' } }}
                  menuItemProps={{ sx: { textTransform: 'none' } }}
              />
            </Box>
            <Button variant={'contained'} onClick={onAIHelpClick} startIcon={<AutoAwesomeOutlinedIcon/>}>Ask AI</Button>
          </>}
          <Notification />
          {/* <Message /> */}
          {/* {!downLG && <FullScreen />} */}
          {/* <Customization /> */}
          {/* <Setting /> */}
          <Profile />
        </Box>
      </Box>
  );
}
